/**
 * Upload Progress Toast Hook - Shows polished toast notifications for background uploads
 * 
 * Uses Sonner's custom toast feature to render a beautiful upload progress UI
 * that matches the app's design system.
 */

import { useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { uploadManager, UploadBatch } from "../services/uploadManager";
import { UploadProgressContent } from "../components/UploadProgressContent";

interface ToastState {
  toastId: string | number;
}

export function useUploadProgressToast() {
  const toastStateRef = useRef<Map<string, ToastState>>(new Map());

  const updateToast = useCallback((batch: UploadBatch) => {
    const existingState = toastStateRef.current.get(batch.batchId);
    
    const completedFiles = batch.items.filter(
      item => item.status === "success" || item.status === "error"
    ).length;
    const hasErrors = batch.items.some(item => item.status === "error");
    const allComplete = completedFiles === batch.items.length;
    const uploadingItem = batch.items.find(item => item.status === "uploading");
    const totalFiles = batch.items.length;
    
    // Calculate overall progress
    const totalProgress = batch.items.reduce((sum, item) => sum + item.progress, 0);
    const avgProgress = Math.round(totalProgress / batch.items.length);

    const toastContent = (
      <UploadProgressContent
        batch={batch}
        completedFiles={completedFiles}
        totalFiles={totalFiles}
        avgProgress={avgProgress}
        hasErrors={hasErrors}
        allComplete={allComplete}
        currentFileName={uploadingItem?.file.name}
      />
    );

    const render = () => (
      <div className="bg-popover border border-border rounded-lg p-4 shadow-lg">
        {toastContent}
      </div>
    );

    // Fully successful: brief final toast, then let it (and the batch) go.
    if (allComplete && !hasErrors) {
      if (existingState) {
        toast.custom(render, { id: existingState.toastId, duration: 3000 });
        toastStateRef.current.delete(batch.batchId);
      } else {
        toast.custom(render, { duration: 3000 });
      }
      return;
    }

    // In-progress OR completed-with-errors: keep a persistent, updatable toast so
    // the user can see which images failed and retry them. State is retained, so a
    // retry re-renders into the same toast; dismiss/auto-delete clears it.
    if (existingState) {
      toast.custom(render, { id: existingState.toastId, duration: Infinity });
    } else {
      const toastId = toast.custom(render, { duration: Infinity });
      toastStateRef.current.set(batch.batchId, { toastId });
    }
  }, []);

  useEffect(() => {
    const handleBatchesChange = (batches: UploadBatch[]) => {
      batches.forEach(batch => {
        updateToast(batch);
      });

      // Clean up toasts for batches that no longer exist
      toastStateRef.current.forEach((state, batchId) => {
        if (!batches.find(b => b.batchId === batchId)) {
          toast.dismiss(state.toastId);
          toastStateRef.current.delete(batchId);
        }
      });
    };

    const unsubscribe = uploadManager.subscribe(handleBatchesChange);
    handleBatchesChange(uploadManager.getBatches());

    const toastStateMap = toastStateRef.current;
    return () => {
      unsubscribe();
      toastStateMap.forEach(state => {
        toast.dismiss(state.toastId);
      });
      toastStateMap.clear();
    };
  }, [updateToast]);
}
